import {
  ApiChannelDefault,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Grid } from "@mui/material";
import { useChannels } from "Hooks/useChannels";
import { useMemo, useState } from "react";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { AnalyticsChartStatCard, table10Rows } from "features/analytics";
import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import { AnalyticsSpacing } from "consts";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import {
  CHART_KEY_EMAIL_CAMPAIGNS_TABLE,
  CHART_KEY_EMAIL_PERFORMANCE_COMPOSITION,
  CHART_KEY_EMAIL_SESSIONS_BY_AUDIENCE,
  CHART_KEY_EMAIL_SESSIONS_REVENUE_BY_CAMPAIGN,
  CHART_KEY_EMAIL_TOP_BY_TREND,
} from "features/analytics/constants/chartKeys";

function EmailOverview() {
  const metricToName = useMetricName();
  const dimensionToName = useDimensionName();
  const { channels } = useChannels();
  const emailChannelName = useMemo(() => {
    return (
      channels.find((c) => c._default === ApiChannelDefault.EMAIL)?.name || ""
    );
  }, [channels]);

  const [metric, setMetric] = useState<ApiMetric>(
    ApiMetric.METRIC_SESSIONS_COUNT,
  );
  const [dimension, setDimension] = useState<ApiDimension>(
    ApiDimension.DIMENSION_CAMPAIGN,
  );

  if (emailChannelName === "") {
    return (
      <CenterPage>
        <Loading />
      </CenterPage>
    );
  }

  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={4}>
        <AnalyticsChartStatCard
          chart={{
            name: "",
            dimension: [],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
          }}
          noToolbar
        />
      </Grid>
      <Grid item xs={4}>
        <AnalyticsChartStatCard
          chart={{
            name: "",
            dimension: [],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE],
              },
            ],
          }}
          noToolbar
        />
      </Grid>
      <Grid item xs={4}>
        <AnalyticsChartStatCard
          chart={{
            name: "",
            dimension: [],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_RETURNING_NINETY_DAY_CUSTOMER_PERCENTAGE,
                ],
              },
            ],
          }}
          noToolbar
        />
      </Grid>
      <Grid item xs={8} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_EMAIL_TOP_BY_TREND}
          customTitleFn={(d, m) =>
            `Top 5 ${dimensionToName(d)}s by ${metricToName(m)}`
          }
          chart={{
            name: "Top 5 ... by",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY, dimension],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            limit: 5,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
            ],
            displayOptions: {
              noFilters: true,
              availableMetrics: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
                ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                ApiMetric.METRIC_SESSION_SALES_COUNT,
                ApiMetric.METRIC_REVENUE,
              ],
              availableDimensions: [
                ApiDimension.DIMENSION_CAMPAIGN,
                ApiDimension.DIMENSION_LEAD_AUDIENCE,
              ],
            },
          }}
          onChangeMetric={setMetric}
          onChangeDimension={setDimension}
          metricsOverride={[metric]}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_EMAIL_PERFORMANCE_COMPOSITION}
          chart={{
            name: `Email Performance Composition by`,
            dimension: [dimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            limit: 10,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
            ],
            displayOptions: {
              noFilters: true,
              showTotals: true,
              availableMetrics: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
                ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                ApiMetric.METRIC_SESSION_SALES_COUNT,
                ApiMetric.METRIC_REVENUE,
              ],
              availableDimensions: [
                ApiDimension.DIMENSION_CAMPAIGN,
                ApiDimension.DIMENSION_LEAD_AUDIENCE,
              ],
            },
          }}
          onChangeMetric={setMetric}
          onChangeDimension={setDimension}
          metricsOverride={[metric]}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_EMAIL_SESSIONS_BY_AUDIENCE}
          chart={{
            name: `Sessions & Revenue Levels by Audience`,
            dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            limit: 10,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_EMAIL_SESSIONS_REVENUE_BY_CAMPAIGN}
          chart={{
            name: `Sessions & Revenue Levels by Email Campaign`,
            dimension: [ApiDimension.DIMENSION_CAMPAIGN],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            limit: 10,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_REVENUE],
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_REVENUE,
              ],
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ minHeight: table10Rows }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_EMAIL_CAMPAIGNS_TABLE}
          chart={{
            name: `Email Campaigns`,
            dimension: [ApiDimension.DIMENSION_CAMPAIGN],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: emailChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              rowsPerPage: 10,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default EmailOverview;
